import React from 'react';
import styled, { keyframes } from 'styled-components';
import { useDesktop } from '../../contexts/DesktopContext';

const matrixRain = keyframes`
  0% { transform: translateY(-100vh); }
  100% { transform: translateY(100vh); }
`;

const WallpaperContainer = styled.div<{ wallpaperType: string }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: ${props => getWallpaperBackground(props.wallpaperType)};
  z-index: -1;
  overflow: hidden;
  transition: background 0.5s ease;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: ${props => getWallpaperOverlay(props.wallpaperType)};
    opacity: ${props => props.wallpaperType === 'neon' ? 0.3 : 0.1};
  }
`;

const MatrixChar = styled.div<{ delay: number; left: number; visible: boolean }>`
  position: absolute;
  color: #00ff00;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  left: ${props => props.left}%;
  animation: ${matrixRain} 3s linear infinite;
  animation-delay: ${props => props.delay}s;
  opacity: ${props => props.visible ? 0.3 : 0};
  transition: opacity 0.5s ease;
`;

const getWallpaperBackground = (wallpaper: string): string => {
  const backgrounds: Record<string, string> = {
    cybersecurity: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 50%, #16213e 100%)',
    matrix: 'linear-gradient(135deg, #000000 0%, #001100 50%, #003300 100%)',
    hacker: 'linear-gradient(135deg, #0f0f0f 0%, #1a0033 50%, #330066 100%)',
    terminal: 'linear-gradient(135deg, #000000 0%, #1a1a00 50%, #333300 100%)',
    neon: 'linear-gradient(135deg, #ff00ff 0%, #00ffff 50%, #ffff00 100%)',
    dark: 'linear-gradient(135deg, #000000 0%, #111111 50%, #222222 100%)',
    blue: 'linear-gradient(135deg, #001122 0%, #003366 50%, #0066cc 100%)',
    red: 'linear-gradient(135deg, #220011 0%, #660033 50%, #cc0066 100%)'
  };
  return backgrounds[wallpaper] || backgrounds.cybersecurity;
};

const getWallpaperOverlay = (wallpaper: string): string => {
  const overlays: Record<string, string> = {
    cybersecurity: 'radial-gradient(circle at 20% 80%, rgba(0, 255, 0, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(0, 255, 255, 0.1) 0%, transparent 50%)',
    matrix: 'radial-gradient(circle at 50% 50%, rgba(0, 255, 0, 0.2) 0%, transparent 70%)',
    hacker: 'radial-gradient(circle at 30% 70%, rgba(255, 0, 255, 0.1) 0%, transparent 50%)',
    terminal: 'radial-gradient(circle at 50% 50%, rgba(255, 255, 0, 0.1) 0%, transparent 60%)',
    neon: 'radial-gradient(circle at 25% 25%, rgba(255, 0, 255, 0.3) 0%, transparent 50%), radial-gradient(circle at 75% 75%, rgba(0, 255, 255, 0.3) 0%, transparent 50%)',
    dark: 'none',
    blue: 'radial-gradient(circle at 50% 50%, rgba(0, 100, 255, 0.2) 0%, transparent 60%)',
    red: 'radial-gradient(circle at 50% 50%, rgba(255, 0, 100, 0.2) 0%, transparent 60%)'
  };
  return overlays[wallpaper] || overlays.cybersecurity;
};

const Wallpaper: React.FC = () => {
  const { wallpaper } = useDesktop();
  const matrixChars = '01アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン';
  const columns = 50;
  const showMatrix = wallpaper === 'matrix' || wallpaper === 'cybersecurity';

  return (
    <WallpaperContainer wallpaperType={wallpaper}>
      {showMatrix && Array.from({ length: columns }, (_, i) => (
        <MatrixChar
          key={i}
          delay={Math.random() * 3}
          left={(i / columns) * 100}
          visible={showMatrix}
        >
          {matrixChars[Math.floor(Math.random() * matrixChars.length)]}
        </MatrixChar>
      ))}
    </WallpaperContainer>
  );
};

export default Wallpaper;
